import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { observer } from "mobx-react-lite";
import { Paperclip, Sparkles, Eye, Info, FolderUp } from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useChatStore } from "@/hooks/useChatStore";
import { useToast } from "@/hooks/use-toast";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { FileTypeIcon } from "@/components/ui/documents/file-type-icon";
import { canPreviewFile } from "@/utils/previewUtils";
import { mirrorToBackups, rowOwnsUploadedObject } from "@/stores/uploadQueue/backupMirror";
import {
  fetchChatbotAttachments,
  subscribeChatbotAttachments,
  type ChatbotAttachment,
} from "@/stores/chat/repository";
import type { PreviewableDoc } from "./DocLink/useDocumentPreview";
import { FolderPickerDialog, type PickedFolder } from "./FolderPickerDialog";
import { v4 as uuidv4 } from "uuid";
import { capture } from "@/lib/sentry/capture";
import {
  BuildingStorageQuotaError,
  cancelBuildingStorageUpload,
  invalidateBuildingStorageQuota,
  reserveBuildingStorageUploads,
  storageAdmissionErrorFrom,
} from "@/domains/documents/services/buildingStorageQuota";
import { discardBuildingStorageUpload } from "@/domains/documents/services/discardBuildingStorageUpload";

export const CHATBOT_FILES_PANEL_QUERY_KEY = "chatbot-files-panel";

interface ChatbotFilesPanelProps {
  buildingId: string;
  open: boolean;
  onClose: () => void;
  /** WHA-3361: open the preview viewer for a doc the user clicked on.
   *  Wired by the parent so the existing DocumentViewerDialog (already
   *  mounted alongside chat doc-link previews) is reused. */
  onPreview?: (doc: PreviewableDoc) => void;
}

// WHA-3380: row type comes from the repository so we don't keep a stale local
// copy of `public.documents` columns in the panel.
type PanelDocument = ChatbotAttachment;

const DOCUMENTS_BUCKET = "documents";

class PromotionValidationError extends Error {}

const PROMOTION_REGISTRATION_ACTIONS = new Set([
  "created",
  "versioned",
  "copied_with_extract",
  "copied_with_artifacts",
  "skipped",
  "resurrected",
]);

function validPromotionRegistrationAction(action: unknown): action is string {
  return typeof action === "string" && PROMOTION_REGISTRATION_ACTIONS.has(action);
}

function promotionFailureDescription(
  error: unknown,
  expectedError: boolean,
  registration: { copied: boolean; canDiscardCopy: boolean; reservationId: string | null }
): string {
  if (registration.copied && !registration.canDiscardCopy && registration.reservationId) {
    return "Could not confirm whether this file was added. Check the project files before trying again.";
  }
  if (expectedError && error instanceof Error) return error.message;
  return "Could not save this file to the project. Please try again.";
}

function registeredPromotionAction(data: unknown): string {
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    throw new Error("Invalid document registration response");
  }
  const row = data as Record<string, unknown>;
  const action = row.action;
  if (
    typeof row.document_id !== "number" ||
    !Number.isSafeInteger(row.document_id) ||
    row.document_id <= 0 ||
    !validPromotionRegistrationAction(action)
  ) {
    throw new Error("Invalid document registration response");
  }
  return action;
}

/** Build the same storage key shape the chatbot's add_file_to_folder uses
 *  (`{org}/{building}/chatbot-uploads/{uuid}.{ext}`) so promoted files end up
 *  in a recognizable namespace separate from their hidden-project origin. */
function buildPromotionStorageKey(
  orgId: string,
  buildingId: string,
  sourceFilePath: string
): string {
  const ext = sourceFilePath.includes(".")
    ? "." + (sourceFilePath.split(".").pop() ?? "").toLowerCase()
    : "";
  return `${orgId}/${buildingId}/chatbot-uploads/${crypto.randomUUID().replace(/-/g, "")}${ext}`;
}

/**
 * WHA-3353: dialog listing every file the current user has either uploaded
 * to the chatbot or had Franklin generate for them, scoped to the current
 * building. Mirrors the `chat_sessions` user-scoping pattern — other users
 * in the same building cannot see these entries.
 *
 * WHA-3361: rows are clickable to preview when the filetype is previewable,
 * and use the same FileTypeIcon as the regular DocumentList / DocumentPicker.
 */
export const ChatbotFilesPanel = observer(function ChatbotFilesPanel({
  buildingId,
  open,
  onClose,
  onPreview,
}: ChatbotFilesPanelProps) {
  const { userProfile } = useAuth();
  const { currentOrganization, isReady: orgReady } = useOrganizationContext();
  const queryClient = useQueryClient();
  const chatStore = useChatStore();
  const { toast } = useToast();
  const [promoting, setPromoting] = useState<PanelDocument | null>(null);
  const [promotionInFlight, setPromotionInFlight] = useState(false);

  const orgId = currentOrganization?.id;
  const userId = userProfile?.id;

  // WHA-3380: data + subscription live in src/stores/chat/repository.ts so the
  // panel doesn't reach into supabase-js directly. `isReady` from
  // useOrganizationContext gates the query during org switches so we never
  // fire with a stale orgId (WHA-3360).
  const queryKey = [CHATBOT_FILES_PANEL_QUERY_KEY, orgId, buildingId, userId];
  const { data: documents = [], isLoading } = useQuery<PanelDocument[]>({
    queryKey,
    enabled: Boolean(open && orgReady && orgId && buildingId && userId),
    staleTime: 0,
    refetchOnMount: "always",
    queryFn: () => {
      // `enabled` above already gates on a truthy orgId/userId; this guard
      // re-establishes that for the type checker without a non-null assertion.
      if (!orgId || !userId) return [];
      return fetchChatbotAttachments({
        orgId,
        buildingId,
        userId: Number(userId),
      });
    },
  });

  // Live-update the panel as new docs land — covers both direct uploads
  // (inserted by the frontend) and Franklin-generated PDFs (inserted by
  // the chatbot service). Subscription scoped by current user so we ignore
  // activity from other users in the same building.
  useEffect(() => {
    if (!open || !orgReady || !userId) return;
    return subscribeChatbotAttachments({
      buildingId,
      userId: Number(userId),
      onChange: () => {
        void queryClient.invalidateQueries({ queryKey });
      },
    });
    // queryKey is stable for a given (orgId, buildingId, userId), and we
    // depend on those scalars explicitly so the channel doesn't churn on
    // every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, orgReady, orgId, buildingId, userId]);

  const uploaded = documents.filter((d) => !d.created_by_chatbot);
  const generated = documents.filter((d) => d.created_by_chatbot);

  const handleAttach = (doc: PanelDocument) => {
    chatStore.addAttachedDocument({
      id: String(doc.id),
      filename: doc.filename,
      state: doc.state,
    });
    onClose();
  };

  const handlePreview = (doc: PanelDocument) => {
    if (!onPreview) return;
    onPreview({
      id: doc.id,
      filename: doc.filename,
      file_path: doc.file_path,
      building_id: buildingId,
      content_type: doc.content_type,
      size: doc.size,
      created_at: doc.created_at ?? new Date().toISOString(),
    });
    onClose();
  };

  const handlePromote = async (target: PickedFolder) => {
    if (!promoting || !orgId || !userId) return;
    setPromotionInFlight(true);
    const numericBuildingId = Number(buildingId);
    let reservationId: string | null = null;
    let copied = false;
    let canDiscardCopy = true;
    let destinationPath: string | null = null;
    try {
      if (!/^[1-9]\d*$/.test(buildingId) || !Number.isSafeInteger(numericBuildingId)) {
        throw new PromotionValidationError("Invalid building ID. Please try again.");
      }
      // 1. Duplicate the storage object into the destination's namespace.
      // Mirrors chatbot/tools/file_upload.py:_copy_storage_object — each
      // documents row owns its own storage key so deleting the chatbot
      // attachment can't orphan the promoted copy.
      destinationPath = buildPromotionStorageKey(orgId, buildingId, promoting.file_path);
      const { data: fileInfo, error: infoError } = await supabase.storage
        .from(DOCUMENTS_BUCKET)
        .info(promoting.file_path);
      const fileSize = fileInfo?.size;
      if (
        infoError ||
        typeof fileSize !== "number" ||
        !Number.isSafeInteger(fileSize) ||
        fileSize < 0
      ) {
        throw new PromotionValidationError(
          "Could not determine this file's size. Please try again."
        );
      }
      [{ reservationId }] = await reserveBuildingStorageUploads(numericBuildingId, [
        {
          idempotencyKey: uuidv4(),
          path: destinationPath,
          size: fileSize,
        },
      ]);
      const { error: copyError } = await supabase.storage
        .from(DOCUMENTS_BUCKET)
        .copy(promoting.file_path, destinationPath);
      if (copyError) {
        throw storageAdmissionErrorFrom(copyError) ?? copyError;
      }
      copied = true;

      // 2. Insert the new documents row. p_copy_from_document_id = source.id
      // triggers the WHA-3370 artifact-copy path on terminal-state sources,
      // so the pipeline doesn't re-process the promoted row.
      canDiscardCopy = false;
      const { data, error } = await supabase.rpc("create_document_with_versioning", {
        p_org_id: orgId,
        p_building_id: numericBuildingId,
        p_project_id: parseInt(target.projectId, 10),
        p_folder_id: target.folderId ? parseInt(target.folderId, 10) : null,
        p_filename: promoting.filename,
        p_file_path: destinationPath,
        p_content_type: promoting.content_type,
        p_size: fileSize,
        p_content_hash: promoting.content_hash,
        p_created_by: Number(userId),
        p_source: "direct_upload",
        p_parent_document_id: null,
        p_container_type: null,
        p_force_version_group: null,
        p_copy_from_document_id: promoting.id,
      });
      if (error) {
        const expectedError = storageAdmissionErrorFrom(error);
        canDiscardCopy = expectedError !== null;
        throw expectedError ?? error;
      }

      const action = registeredPromotionAction(data);
      const isDuplicate = action === "skipped";
      if (action === "skipped" || action === "resurrected") {
        const completedReservationId = reservationId;
        reservationId = null;
        if (completedReservationId) {
          await discardBuildingStorageUpload(
            destinationPath,
            completedReservationId,
            numericBuildingId
          );
        }
      }
      reservationId = null;
      invalidateBuildingStorageQuota(buildingId);

      // 3. The promoted copy is a new storage object, so it needs its own entry
      // in the write-once backups bucket — but only now that a row owns it. A
      // backup can never be deleted, so mirroring before the insert would make
      // every failed promotion add a permanent copy of the file. `skipped` and
      // `resurrected` answer with a row that keeps its own file_path, leaving
      // the copy above orphaned in `documents` — bad, but recoverable there;
      // backing it up would not be.
      if (rowOwnsUploadedObject(action)) {
        await mirrorToBackups(destinationPath);
      }

      toast({
        title: isDuplicate ? "Already there" : "Saved to project",
        description: isDuplicate
          ? `${promoting.filename} already exists in ${target.projectName}${
              target.folderPath ? ` ${target.folderPath}` : ""
            }.`
          : `${promoting.filename} is now in ${target.projectName}${
              target.folderPath ? ` ${target.folderPath}` : ""
            }.`,
      });
      setPromoting(null);
    } catch (err) {
      if (reservationId) {
        if (copied && canDiscardCopy && destinationPath) {
          await discardBuildingStorageUpload(destinationPath, reservationId, numericBuildingId);
        } else if (!copied) {
          try {
            await cancelBuildingStorageUpload(reservationId, numericBuildingId);
          } catch (cancelError) {
            capture(cancelError instanceof Error ? cancelError : new Error(String(cancelError)), {
              tags: { feature: "building-storage-quota", stage: "cancel-copy" },
              fingerprint: ["building-storage-quota", "cancel-copy"],
              context: { quota: { buildingId: numericBuildingId } },
            });
          }
        }
      }
      const expectedError =
        err instanceof BuildingStorageQuotaError || err instanceof PromotionValidationError;
      if (!expectedError) {
        capture(err instanceof Error ? err : new Error(String(err)), {
          tags: { feature: "chatbot-document-promotion", stage: "promote" },
          fingerprint: ["chatbot-document-promotion", "promote"],
          context: { promotion: { buildingId: numericBuildingId } },
        });
      }
      toast({
        variant: "destructive",
        title: "Couldn't save to project",
        description: promotionFailureDescription(err, expectedError, {
          copied,
          canDiscardCopy,
          reservationId,
        }),
      });
    } finally {
      setPromotionInFlight(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent
          className="max-w-md"
          // Radix Dialog auto-focuses the first focusable child on open, which
          // landed on the Info tooltip trigger and opened the tooltip
          // unprompted. Suppress auto-focus so the tooltip only appears on
          // hover (keyboard users can still Tab to it to read it).
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-1.5">
              <span>Your Franklin files</span>
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label="What is this panel?"
                      className="inline-flex h-5 w-5 items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600"
                    >
                      <Info className="h-3.5 w-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs text-xs leading-snug">
                    These are files you uploaded to the chatbot, or that Franklin generated for you.
                    They are NOT stored in this building's projects — use the upload button on a
                    file to save it there.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            {isLoading && <p className="px-1 py-2 text-sm text-neutral-500">Loading…</p>}
            {!isLoading && documents.length === 0 && (
              <p className="px-1 py-2 text-sm text-neutral-500">
                No files yet. Upload a file or ask Franklin to generate one.
              </p>
            )}
            {uploaded.length > 0 && (
              <FileSection
                title="Uploaded"
                docs={uploaded}
                onAttach={handleAttach}
                onPreview={onPreview ? handlePreview : undefined}
                onPromote={(doc) => setPromoting(doc)}
              />
            )}
            {generated.length > 0 && (
              <FileSection
                title="Created by Franklin"
                icon={<Sparkles className="h-3.5 w-3.5 text-primary" />}
                docs={generated}
                onAttach={handleAttach}
                onPreview={onPreview ? handlePreview : undefined}
                onPromote={(doc) => setPromoting(doc)}
              />
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      <FolderPickerDialog
        open={!!promoting}
        onOpenChange={(o) => {
          if (!o && !promotionInFlight) setPromoting(null);
        }}
        buildingId={buildingId}
        filename={promoting?.filename}
        title="Save to a project folder"
        submitLabel={promotionInFlight ? "Saving…" : "Upload here"}
        onSelect={(target) => {
          void handlePromote(target);
        }}
      />
    </>
  );
});

export function FileSection({
  title,
  icon,
  docs,
  onAttach,
  onPreview,
  onPromote,
}: {
  title: string;
  icon?: React.ReactNode;
  docs: PanelDocument[];
  onAttach: (doc: PanelDocument) => void;
  onPreview?: (doc: PanelDocument) => void;
  onPromote?: (doc: PanelDocument) => void;
}) {
  return (
    <div className="mb-3">
      <div className="mb-1.5 flex items-center gap-1.5 px-1 text-xs font-semibold uppercase tracking-wide text-neutral-500">
        {icon}
        <span>{title}</span>
        <span className="ml-1 rounded bg-neutral-100 px-1 text-[10px] font-medium text-neutral-600">
          {docs.length}
        </span>
      </div>
      <TooltipProvider delayDuration={75}>
        <ul className="space-y-1">
          {docs.map((doc) => {
            const previewable = !!onPreview && canPreviewFile(doc.filename, doc.content_type);
            return (
              <li
                key={doc.id}
                className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-neutral-100"
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => onAttach(doc)}
                      className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-left"
                      aria-label={`Add ${doc.filename} to chat`}
                    >
                      <FileTypeIcon
                        filename={doc.filename}
                        contentType={doc.content_type}
                        className="h-7 w-7 shrink-0"
                      />
                      <span className="min-w-0 flex-1 truncate text-sm text-neutral-700">
                        {doc.filename}
                      </span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-xs">
                    Add to chat
                  </TooltipContent>
                </Tooltip>
                {previewable && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => onPreview?.(doc)}
                        aria-label={`Preview ${doc.filename}`}
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="text-xs">
                      Preview
                    </TooltipContent>
                  </Tooltip>
                )}
                {onPromote && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => onPromote(doc)}
                        aria-label={`Save ${doc.filename} to a project folder`}
                      >
                        <FolderUp className="h-3.5 w-3.5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="text-xs">
                      Upload to project
                    </TooltipContent>
                  </Tooltip>
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => onAttach(doc)}
                      aria-label={`Attach ${doc.filename} to next message`}
                    >
                      <Paperclip className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-xs">
                    Attach to next message
                  </TooltipContent>
                </Tooltip>
              </li>
            );
          })}
        </ul>
      </TooltipProvider>
    </div>
  );
}

ChatbotFilesPanel.displayName = "ChatbotFilesPanel";
